import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Search } from "lucide-react";
import { api, postJSON } from "../api";
import type { InboundLabelAccount, InboundLabelShipment } from "../types";
import { staffInboundStatus } from "../inbound-label-status";

export function StaffInboundLabels() {
  const [accounts, setAccounts] = useState<InboundLabelAccount[]>([]);
  const [shipments, setShipments] = useState<InboundLabelShipment[]>([]);
  const [query, setQuery] = useState("");
  const [searched, setSearched] = useState(false);
  const [editing, setEditing] = useState<InboundLabelAccount | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [queueError, setQueueError] = useState("");
  const loadQueue = () =>
    api<{ shipments: InboundLabelShipment[] }>("/api/staff/inbound-labels")
      .then((result) => {
        setShipments(result.shipments);
        setQueueError("");
      })
      .catch(() =>
        setQueueError(
          "Could not load inbound purchases. Try refreshing the queue.",
        ),
      );
  useEffect(() => {
    void loadQueue();
  }, []);
  async function search(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await api<{ accounts: InboundLabelAccount[] }>(
        `/api/staff/accounts?query=${encodeURIComponent(query.trim())}`,
      );
      setAccounts(result.accounts);
      setSearched(true);
      setEditing(null);
    } catch (value) {
      setError(value instanceof Error ? value.message : "Search failed.");
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    if (!editing) return;
    setBusy(true);
    setError("");
    try {
      const { account } = await postJSON<{ account: InboundLabelAccount }>(
        `/api/staff/accounts/${editing.userId}/inbound-label-access`,
        { enabled: !editing.inboundLabelsEnabled, approvalNote: note.trim() },
      );
      setAccounts((current) =>
        current.map((item) =>
          item.userId === account.userId ? account : item,
        ),
      );
      setEditing(null);
      setMessage(
        `${account.email}: inbound label access ${account.inboundLabelsEnabled ? "enabled" : "revoked"}.`,
      );
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Could not update access.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function reconcile(shipment: InboundLabelShipment) {
    setBusy(true);
    setError("");
    try {
      await postJSON(`/api/staff/inbound-labels/${shipment.id}/reconcile`, {});
      await loadQueue();
      setMessage(
        "Original purchase checked. No new customer payment was submitted.",
      );
    } catch (value) {
      setError(
        value instanceof Error
          ? value.message
          : "Could not reconcile this purchase.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="page page--staff-inbound">
      <Link className="back-link" to="/staff">
        <ArrowLeft size={16} />
        Warehouse queue
      </Link>
      <header className="page-head">
        <div>
          <span className="mono-label">STAFF ACCESS CONTROL</span>
          <h1>Inbound shipping labels</h1>
          <p>
            Enable approved customers, then follow their packages to RX Routing.
            Access is off by default.
          </p>
        </div>
      </header>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="payment-message" role="status">
          {message}
        </p>
      )}
      <section className="inbound-panel">
        <h2>Customer access</h2>
        <p>
          Before enabling access, confirm the originating warehouse accepts a
          prepaid label and permits transfer to RX Routing. Record that approval
          below.
        </p>
        <form className="inbound-account-search" onSubmit={search}>
          <label>
            Find customer by email or name
            <input
              type="search"
              required
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Customer email or name"
            />
          </label>
          <button className="button button--primary" disabled={busy}>
            <Search size={16} />
            {busy ? "Searching…" : "Search accounts"}
          </button>
        </form>
        {searched && accounts.length === 0 && (
          <p role="status">
            No matching accounts. Try the customer’s full email address.
          </p>
        )}
        <ul className="inbound-account-list">
          {accounts.map((account) => (
            <li key={account.userId}>
              <div>
                <b>{account.fullName || "Customer"}</b>
                <span>{account.email}</span>
                <small>
                  {account.inboundLabelsEnabled
                    ? "Access enabled"
                    : "Access not enabled"}
                  {account.approvalNote ? ` · ${account.approvalNote}` : ""}
                </small>
              </div>
              <button
                className="button button--outline"
                disabled={busy}
                aria-label={`${account.inboundLabelsEnabled ? "Revoke" : "Enable"} inbound labels for ${account.email}`}
                onClick={() => {
                  setEditing(account);
                  setNote("");
                  setMessage("");
                }}
              >
                {account.inboundLabelsEnabled
                  ? "Revoke access"
                  : "Enable access"}
              </button>
            </li>
          ))}
        </ul>
        {editing && (
          <form
            className="inbound-access-confirm"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
            aria-label="Confirm account access change"
          >
            <h3>
              {editing.inboundLabelsEnabled ? "Revoke" : "Enable"} access for{" "}
              {editing.email}?
            </h3>
            <p>
              {editing.inboundLabelsEnabled
                ? "This stops new purchases. Existing paid labels and their downloads remain available to the owner."
                : "This customer will be able to buy separate labels for eligible, authorized forwarding orders."}
            </p>
            <label>
              {editing.inboundLabelsEnabled
                ? "Reason for revoking access"
                : "Warehouse approval note"}
              <textarea
                required
                minLength={3}
                maxLength={1000}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder={
                  editing.inboundLabelsEnabled
                    ? "Why is access changing?"
                    : "Who confirmed acceptance, which warehouse, and when?"
                }
              />
            </label>
            <div className="inbound-actions">
              <button
                type="button"
                className="button button--outline"
                disabled={busy}
                onClick={() => setEditing(null)}
              >
                Cancel
              </button>
              <button
                className="button button--primary"
                disabled={busy || note.trim().length < 3}
              >
                {busy
                  ? "Saving…"
                  : editing.inboundLabelsEnabled
                    ? "Confirm revocation"
                    : "Confirm enable access"}
              </button>
            </div>
          </form>
        )}
      </section>
      <section className="inbound-panel inbound-queue">
        <header>
          <div>
            <h2>Inbound purchases</h2>
            <p>
              Carrier delivery is not warehouse receipt. Check the parcel in
              through the normal intake workflow.
            </p>
          </div>
          <button
            className="button button--outline"
            disabled={busy}
            onClick={() => void loadQueue()}
          >
            <RefreshCw size={16} />
            Refresh queue
          </button>
        </header>
        {queueError && (
          <p role="alert" className="form-error">
            {queueError}
          </p>
        )}
        {!queueError && shipments.length === 0 && (
          <p>No inbound-label purchases to review.</p>
        )}
        <ul className="inbound-account-list">
          {shipments.map((shipment) => {
            const status = staffInboundStatus(shipment);
            return <li key={shipment.id} className={status.attention ? "inbound-queue__attention" : undefined}>
              <div>
                <b>
                  {shipment.rxid} ·{" "}
                  {shipment.origin.company || shipment.origin.name}
                </b>
                <span>{shipment.warehouseReference}</span>
                <span className="inbound-queue__status">{status.title}</span>
                <small>
                  {shipment.trackingStatus.toLowerCase() === "delivered"
                    ? "Confirm receipt in the warehouse queue"
                    : shipment.trackingStatus.replaceAll('_', ' ') || "No carrier scan"}
                  {shipment.trackingCode ? ` · ${shipment.trackingCode}` : ""}
                </small>
                {shipment.message && <small>{shipment.message}</small>}
              </div>
              {status.action && <button
                className="button button--outline"
                disabled={busy}
                onClick={() => void reconcile(shipment)}
              >
                {status.action}
              </button>}
            </li>
          })}
        </ul>
      </section>
    </div>
  );
}
